import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import AddButton from "../../buttons/button";
import CloseIcon from "../../../assets/icons/close-icon";
import { useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { goodsCrDBNotesAction } from "../../../redux/actions/purchase-action";
import moment from "moment";
import LoadingSpinner from "../../helper-components/loading-spinner";
import errorToast from "../../helper-components/error-toast";

// Document total field differs per screen (invoice, GRN, order, return, credit note)
const TOTAL_FIELDS = [
  "poit_total_amount",
  "gort_total_amount",
  "pot_total_amount",
  "prt_total_amount",
  "pct_total_amount",
];

export const getInvoiceTotal = (values) => {
  const key = TOTAL_FIELDS.find((field) => values?.[field] !== undefined);
  return parseFloat(values?.[key]) || 0;
};

// Sum of adjusted amounts on checked notes, optionally skipping one row
export const getAdjustedTotal = (rows = [], skipIndex = -1) =>
  rows.reduce(
    (sum, row, idx) =>
      idx !== skipIndex && row?.isChecked
        ? sum + (parseFloat(row?.adjusted_amount) || 0)
        : sum,
    0
  );

const round2 = (num) => Math.round(num * 100) / 100;

// Checked notes sent on save; remaining_amount is the balance left after this adjustment
export const buildCreditNotePayload = (rows = []) =>
  rows
    .filter((note) => note?.isChecked)
    .map((note) => {
      const remaining = parseFloat(note?.remaining_amount) || 0;
      const adjusted = parseFloat(note?.adjusted_amount) || 0;
      return {
        invoice_number: note?.invoice_number ?? "",
        invoice_total_amount: parseFloat(note?.invoice_total_amount) || 0,
        remaining_amount: round2(remaining - adjusted),
        adjusted_amount: adjusted,
        claimed_amount: parseFloat(note?.claimed_amount) || 0,
        // DB column is a date - send YYYY-MM-DD, not a JS Date string
        invoice_date: note?.invoice_date
          ? moment.utc(new Date(note.invoice_date)).format("YYYY-MM-DD")
          : "",
        remarks: note?.remarks ?? "",
      };
    });

// A note can be adjusted up to its remaining balance, and the checked notes
// together cannot exceed the invoice amount
const getMaxAdjustable = (rows, index, invoiceTotal) =>
  round2(
    Math.max(
      0,
      Math.min(
        parseFloat(rows[index]?.remaining_amount) || 0,
        invoiceTotal - getAdjustedTotal(rows, index)
      )
    )
  );

const clampRow = (rows, index, invoiceTotal) => {
  const max = getMaxAdjustable(rows, index, invoiceTotal);
  const current = parseFloat(rows[index]?.adjusted_amount);
  rows[index] = {
    ...rows[index],
    adjusted_amount: isNaN(current) ? max : Math.min(Math.max(current, 0), max),
  };
};

const formatAmount = (value) =>
  (parseFloat(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) =>
  value ? moment.utc(new Date(value)).format("DD-MM-YYYY") : "-";

export default function PurchaseCreditNotePanel({ open, setOpen, page = "" }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext();
  const notes = values?.creditNoteData || [];
  const invoiceTotal = getInvoiceTotal(values);
  const adjustedTotal = getAdjustedTotal(notes);
  const selectedCount = notes.filter((note) => note?.isChecked).length;
  const allSelected = notes.length > 0 && selectedCount === notes.length;
  const isView = page === "view";

  useEffect(() => {
    if (open && !isView) {
      setLoading(true);
      dispatch(goodsCrDBNotesAction(values?.cmr_id, onSuccess, onError));
    }
  }, [open]);

  const onSuccess = (data) => {
    setLoading(false);
    // Keep notes already selected for this invoice when the panel is reopened
    const selectedNotes = notes.filter((note) => note?.isChecked);
    const creditNoteData = (data?.data?.creditNoteData || []).map((item) => {
      const selected = selectedNotes.find(
        (note) => note?.invoice_number === item?.invoice_number
      );
      return selected
        ? {
            ...item,
            isChecked: true,
            adjusted_amount: selected.adjusted_amount,
            remarks: selected.remarks,
          }
        : { ...item, isChecked: false };
    });
    const creditNoteHeaderData = data?.data?.headerdata;
    setFieldValue(`creditNoteData`, creditNoteData);
    setFieldValue(`claimed_adjusted`, creditNoteHeaderData?.claimed_adjusted);
    setFieldValue(`ledger_closing`, creditNoteHeaderData?.ledger_closing);
    setFieldValue(`pcn_net_balance`, creditNoteHeaderData?.pcn_net_balance);
    setFieldValue(`pcns_total`, creditNoteHeaderData?.pcns_total);
    setFieldValue(`remaining_balance`, creditNoteHeaderData?.remaining_balance);
  };
  const onError = () => {
    setLoading(false);
    setFieldValue(`creditNoteData`, []);
  };

  const toggleAll = (isChecked) => {
    const updatedData = notes.map((item) => ({ ...item, isChecked: false }));
    if (isChecked) {
      // Fill notes in order until the invoice amount is used up
      updatedData.forEach((item, idx) => {
        clampRow(updatedData, idx, invoiceTotal);
        updatedData[idx].isChecked = true;
      });
    }
    setFieldValue("creditNoteData", updatedData);
  };

  const toggleRow = (index, isChecked) => {
    const updatedData = [...notes];
    if (isChecked) clampRow(updatedData, index, invoiceTotal);
    updatedData[index] = { ...updatedData[index], isChecked };
    setFieldValue("creditNoteData", updatedData);
  };

  const changeAmount = (index, rawValue) => {
    const newValue = parseFloat(rawValue);
    const updatedData = [...notes];
    updatedData[index] = {
      ...updatedData[index],
      // Typing an amount selects the note
      isChecked: true,
      adjusted_amount: isNaN(newValue)
        ? ""
        : Math.min(
            Math.max(newValue, 0),
            getMaxAdjustable(notes, index, invoiceTotal)
          ),
    };
    setFieldValue("creditNoteData", updatedData);
  };

  const changeRemarks = (index, remarks) => {
    const updatedData = [...notes];
    updatedData[index] = { ...updatedData[index], remarks };
    setFieldValue("creditNoteData", updatedData);
  };

  const applyAdjustment = () => {
    if (round2(adjustedTotal) > round2(invoiceTotal)) {
      errorToast("Adjusted amount cannot be more than the invoice amount");
      return;
    }
    setOpen(false);
  };

  // Plain words instead of Dr/Cr - "Cr" is easily read as crores.
  // Ledger balance: positive = we owe the vendor, negative = vendor owes us
  const ledgerBalance = parseFloat(values?.ledger_closing) || 0;
  const summary = [
    { label: "Vendor Payable", value: values?.remaining_balance },
    { label: "Total Credit Notes", value: values?.pcns_total },
    { label: "Already Adjusted", value: values?.claimed_adjusted },
    {
      label: "Available Credit",
      value: values?.pcn_net_balance,
      highlight: true,
    },
    {
      label: "Ledger Balance",
      value: Math.abs(ledgerBalance),
      suffix:
        ledgerBalance > 0
          ? "To Pay"
          : ledgerBalance < 0
          ? "To Receive"
          : "",
    },
  ];

  const inputClass =
    "h-[32px] w-full px-2 border border-[var(--slide-panel-border)] rounded-md bg-white text-[length:var(--create-table-row-font-size)] text-[var(--create-table-row-fg-color)] focus:outline-none focus:border-[var(--primary)] disabled:bg-transparent disabled:border-transparent";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full w-[400px] sm:w-[70%] bg-white flex flex-col rounded-tl-lg rounded-bl-lg shadow-xl">
                {/* Header */}
                <div
                  className="h-[61px] shrink-0 bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b"
                  style={{ borderColor: "var(--slide-panel-border)" }}
                >
                  <div>
                    <div
                      style={{
                        color: "var(--slide-panel-h-fg)",
                        fontSize: "var(--slide-panel-h-fs)",
                        fontWeight: "var(--slide-panel-h-fw)",
                      }}
                    >
                      Credit / Debit Notes
                    </div>
                    {values?.cmr_name?.label && (
                      <div className="text-xs text-[var(--slide-panel-label)]">
                        {values.cmr_name.label}
                      </div>
                    )}
                  </div>
                  <AddButton
                    onclkFn={() => setOpen(false)}
                    icon={
                      <CloseIcon
                        width="12px"
                        height="12px"
                        fill="var(--close-button-fg)"
                      />
                    }
                    text={
                      <span>
                        Close <span className="opacity-50">(X)</span>
                      </span>
                    }
                    className="flex h-[32px] items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                  />
                </div>

                {loading ? (
                  <div className="flex-1">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="flex-1 min-h-0 flex flex-col px-4 pt-4 gap-4">
                    {/* Vendor summary */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 shrink-0">
                      {summary.map((item) => (
                        <div
                          key={item.label}
                          className={`rounded-lg border px-3 py-2 ${
                            item.highlight
                              ? "border-[var(--success-color)] bg-[#ecfdf3]"
                              : "border-[var(--slide-panel-border)] bg-white"
                          }`}
                        >
                          <div className="text-xs text-[var(--slide-panel-label)]">
                            {item.label}
                          </div>
                          <div
                            className={`text-sm font-semibold mt-0.5 ${
                              item.highlight
                                ? "text-[var(--success-color)]"
                                : "text-[var(--create-table-row-fg-color)]"
                            }`}
                          >
                            ₹{formatAmount(item.value)}
                            {item.suffix && (
                              <span className="ml-1 text-xs font-normal text-[var(--slide-panel-label)]">
                                {item.suffix}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Notes */}
                    <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-[var(--slide-panel-border)]">
                      <table className="w-full border-collapse">
                        <thead className="sticky top-0 z-[10] bg-[var(--create-table-header-bg)]">
                          <tr className="h-[44px] text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]">
                            {heading.map((col) => (
                              <th
                                key={col.value}
                                style={{ minWidth: col.minWidth }}
                                className={`px-3 whitespace-nowrap ${
                                  col.align === "right"
                                    ? "text-right"
                                    : "text-left"
                                }`}
                              >
                                {col.value === "action" ? (
                                  <input
                                    type="checkbox"
                                    disabled={isView || notes.length === 0}
                                    checked={allSelected}
                                    onChange={(e) => toggleAll(e.target.checked)}
                                    className="custom-checkbox form-check-input"
                                  />
                                ) : (
                                  col.label
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="text-[length:var(--create-table-row-font-size)] text-[var(--create-table-row-fg-color)]">
                          {notes.length > 0 ? (
                            notes.map((note, index) => (
                              <tr
                                key={note?.invoice_number || index}
                                className={`h-[48px] border-b border-[var(--slide-panel-border)] ${
                                  note?.isChecked
                                    ? "bg-[var(--create-table-row-sel-bg)]"
                                    : "hover:bg-[var(--create-table-row-hover-bg)]"
                                }`}
                              >
                                <td className="px-3">
                                  <input
                                    type="checkbox"
                                    disabled={isView}
                                    checked={note?.isChecked || false}
                                    onChange={(e) =>
                                      toggleRow(index, e.target.checked)
                                    }
                                    className="custom-checkbox form-check-input"
                                  />
                                </td>
                                <td className="px-3 whitespace-nowrap font-medium">
                                  {note?.invoice_number}
                                </td>
                                <td className="px-3 whitespace-nowrap">
                                  {formatDate(note?.invoice_date)}
                                </td>
                                <td className="px-3 text-right">
                                  {formatAmount(note?.invoice_total_amount)}
                                </td>
                                <td className="px-3 text-right text-[var(--slide-panel-label)]">
                                  {formatAmount(note?.claimed_amount)}
                                </td>
                                <td className="px-3 text-right font-medium">
                                  {formatAmount(note?.remaining_amount)}
                                </td>
                                <td className="px-3">
                                  <input
                                    disabled={isView}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    placeholder="0.00"
                                    value={
                                      note?.isChecked
                                        ? note?.adjusted_amount ?? ""
                                        : ""
                                    }
                                    onChange={(e) =>
                                      changeAmount(index, e.target.value)
                                    }
                                    className={`${inputClass} text-right input-field-no-spin-buttons`}
                                  />
                                </td>
                                <td className="px-3">
                                  <input
                                    disabled={isView}
                                    type="text"
                                    placeholder="Add remark"
                                    value={note?.remarks || ""}
                                    onChange={(e) =>
                                      changeRemarks(index, e.target.value)
                                    }
                                    className={inputClass}
                                  />
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={heading.length}
                                className="text-center py-10 text-[var(--slide-panel-label)]"
                              >
                                No credit notes available for this vendor.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div
                  className="shrink-0 flex justify-between items-center gap-4 px-4 py-3 mt-4 border-t"
                  style={{ borderColor: "var(--slide-panel-border)" }}
                >
                  <AddButton
                    prefixIcon={false}
                    onclkFn={() => setOpen(false)}
                    text={
                      <span>
                        Cancel <span className="opacity-50">(C)</span>
                      </span>
                    }
                    className="flex h-[36px] items-center gap-2 bg-[var(--close-button-bg)] border-radius px-4 text-[var(--close-button-fg)]"
                  />

                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-[var(--slide-panel-label)]">
                        Invoice Amount
                      </span>
                      <span className="ml-2 font-semibold">
                        ₹{formatAmount(invoiceTotal)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[var(--slide-panel-label)]">
                        Notes Selected
                      </span>
                      <span className="ml-2 font-semibold">{selectedCount}</span>
                    </div>
                    <div>
                      <span className="text-[var(--slide-panel-label)]">
                        Adjusting
                      </span>
                      <span className="ml-2 font-semibold text-[var(--success-color)]">
                        ₹{formatAmount(adjustedTotal)}
                      </span>
                    </div>
                    {!isView && invoiceTotal <= 0 && (
                      <span className="text-xs text-[var(--error-color)]">
                        Add items to the invoice before adjusting notes
                      </span>
                    )}
                  </div>

                  {!isView ? (
                    <AddButton
                      onclkFn={applyAdjustment}
                      prefixIcon={false}
                      text={
                        <span>
                          Apply Adjustment{" "}
                          <span className="opacity-50">(A)</span>
                        </span>
                      }
                      className="flex items-center px-[24px] h-[40px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)]"
                    />
                  ) : (
                    <div />
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

const heading = [
  { label: "", value: "action", minWidth: "40px" },
  { label: "Note No.", value: "invoice_number", minWidth: "190px" },
  { label: "Date", value: "invoice_date", minWidth: "100px" },
  { label: "Note Amount", value: "invoice_total_amount", align: "right" },
  { label: "Already Adjusted", value: "claimed_amount", align: "right" },
  { label: "Available", value: "remaining_amount", align: "right" },
  {
    label: "Adjust Amount",
    value: "adjusted_amount",
    align: "right",
    minWidth: "130px",
  },
  { label: "Remarks", value: "remarks", minWidth: "160px" },
];
